// Sentry Error Tracking Configuration for Frontend
import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Only enabled in production environment
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;

  // Only initialize if DSN is provided (production/staging)
  if (!dsn) {
    console.log('Sentry DSN not configured - error tracking disabled');
    return;
  }

  Sentry.init({
    dsn,
    environment,

    // Set sample rate for performance monitoring
    // 0.0 = 0%, 1.0 = 100%
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Set sample rate for session replay
    // Helps debug issues by recording user sessions
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Integrations for React
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Filter out certain errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Filter out browser extension errors
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
      ) {
        if (
          error.message.includes('chrome-extension://') ||
          error.message.includes('moz-extension://') ||
          error.message.includes('safari-extension://')
        ) {
          return null; // Don't send to Sentry
        }
      }

      return event;
    },

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // Additional context
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy breadcrumbs if needed
      if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
        return null;
      }
      return breadcrumb;
    },
  });

  console.log(`Sentry initialized for ${environment} environment`);
};

/**
 * Capture a custom error
 */
export const captureError = (
  error: Error,
  context?: Record<string, unknown>
) => {
  if (context) {
    Sentry.setContext('custom', context);
  }
  Sentry.captureException(error);
};

/**
 * Capture a custom message
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info'
) => {
  Sentry.captureMessage(message, level);
};

/**
 * Set user context for error tracking
 */
export const setSentryUser = (user: {
  id: string;
  email?: string;
  username?: string;
}) => {
  Sentry.setUser(user);
};

/**
 * Clear user context (e.g., on logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};
